import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import api, { resolveImg, formatPrice } from "@/lib/api";
import { useCategories } from "@/hooks/useCategories";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";

export default function Product() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { categories } = useCategories();
  const [product, setProduct] = useState(null);
  const [active, setActive] = useState(0);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setActive(0);
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => setNotFound(true));
  }, [id]);

  const categoryName = product ? (categories.find((c) => c.slug === product.category)?.name || product.category) : "";

  return (
    <div className="min-h-screen bg-[#FAF8F4]" data-testid="product-page">
      <Header />
      <div className="mx-auto max-w-[1400px] px-5 py-10 md:px-10 md:py-16">
        <button
          data-testid="back-button"
          onClick={() => navigate(-1)}
          className="mb-10 inline-flex items-center gap-2 text-sm uppercase tracking-[0.18em] text-stone-500 transition-colors hover:text-wine"
        >
          <ArrowLeft size={18} strokeWidth={1.5} /> Back
        </button>

        {notFound ? (
          <div className="py-24 text-center text-base text-stone-400">This piece could not be found.</div>
        ) : !product ? (
          <div className="py-24 text-center text-sm uppercase tracking-[0.2em] text-stone-400">Loading…</div>
        ) : (
          <div className="grid grid-cols-1 gap-12 md:grid-cols-2 md:gap-16">
            {/* Gallery */}
            <div data-testid="product-gallery">
              <div className="group relative aspect-square overflow-hidden bg-white">
                <div
                  className="flex h-full w-full transition-transform duration-500 ease-out"
                  style={{ transform: `translateX(-${active * 100}%)` }}
                >
                  {product.images.map((img, i) => (
                    <img
                      key={i}
                      src={resolveImg(img)}
                      alt={product.name}
                      className="h-full w-full shrink-0 object-cover"
                      data-testid={i === active ? "main-image" : undefined}
                    />
                  ))}
                </div>
                {product.images?.length > 1 && (
                  <>
                    <button
                      data-testid="gallery-prev"
                      onClick={() => setActive((a) => (a - 1 + product.images.length) % product.images.length)}
                      className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 text-stone-800 opacity-0 shadow-md backdrop-blur transition-opacity duration-300 hover:bg-white group-hover:opacity-100"
                      aria-label="Previous image"
                    >
                      <ChevronLeft size={20} strokeWidth={1.5} />
                    </button>
                    <button
                      data-testid="gallery-next"
                      onClick={() => setActive((a) => (a + 1) % product.images.length)}
                      className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 text-stone-800 opacity-0 shadow-md backdrop-blur transition-opacity duration-300 hover:bg-white group-hover:opacity-100"
                      aria-label="Next image"
                    >
                      <ChevronRight size={20} strokeWidth={1.5} />
                    </button>
                    <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-2">
                      {product.images.map((_, i) => (
                        <button
                          key={i}
                          data-testid={`dot-${i}`}
                          onClick={() => setActive(i)}
                          className={`h-1.5 rounded-full transition-all duration-300 ${active === i ? "w-6 bg-stone-800" : "w-1.5 bg-stone-400"}`}
                          aria-label={`Go to image ${i + 1}`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
              {product.images?.length > 1 && (
                <div className="mt-4 flex gap-3">
                  {product.images.map((img, i) => (
                    <button
                      key={i}
                      data-testid={`thumb-${i}`}
                      onClick={() => setActive(i)}
                      className={`h-20 w-20 overflow-hidden border-2 transition-colors ${active === i ? "border-wine" : "border-transparent hover:border-stone-300"}`}
                    >
                      <img src={resolveImg(img)} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Details */}
            <div className="flex flex-col justify-center md:pl-6">
              <p className="text-xs uppercase tracking-[0.25em] text-stone-400">{categoryName}</p>
              <h1 className="mt-3 font-serif text-3xl tracking-tight text-stone-900 md:text-4xl" data-testid="product-name">{product.name}</h1>
              {product.variants?.length > 0 ? (
                <div className="mt-8 overflow-hidden border border-border" data-testid="variant-table">
                  <table className="w-full text-left">
                    <thead className="border-b border-border bg-white text-xs uppercase tracking-[0.2em] text-stone-500">
                      <tr>
                        <th className="px-5 py-3 font-normal">CTTW</th>
                        <th className="px-5 py-3 text-right font-normal">Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {product.variants.map((v, i) => (
                        <tr key={i} className="border-b border-border last:border-0" data-testid={`variant-row-${i}`}>
                          <td className="px-5 py-3 text-base text-stone-800">{v.cttw}</td>
                          <td className="px-5 py-3 text-right text-base font-bold text-stone-900">{formatPrice(v.price)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="mt-6">
                  {product.cttw && <p className="text-sm uppercase tracking-[0.2em] text-stone-500" data-testid="product-cttw">{product.cttw}</p>}
                  <p className="mt-2 text-3xl font-bold text-stone-900 md:text-4xl" data-testid="product-price">{formatPrice(product.price)}</p>
                </div>
              )}
              {product.description && <p className="mt-8 max-w-md text-base leading-relaxed text-stone-600" data-testid="product-description">{product.description}</p>}
              <div className="mt-10 h-px w-full bg-border" />
              <p className="mt-6 text-sm leading-relaxed text-stone-500">
                For availability, sizing, or a private viewing, please contact our team.
              </p>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
